import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  SimpleChanges,
  forwardRef,
  inject,
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ScrollingModule } from '@angular/cdk/scrolling';

/** Minimal shape a country needs for the dial-code prefix. `CountryData` is compatible. */
export interface PhoneCountryItem {
  code: string;
  name: string;
  dialCode: string;
}

/**
 * Input de teléfono con prefijo de país (bandera SVG flag-icons + código +NN) y buscador.
 * Emite el teléfono completo como una sola cadena "+51 987654321".
 * Implementa ControlValueAccessor → se usa con formControlName.
 */
@Component({
  selector: 'app-phone-input-flag',
  standalone: true,
  imports: [CommonModule, FormsModule, ScrollingModule],
  templateUrl: './phone-input-flag.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneInputFlagComponent),
      multi: true,
    },
  ],
})
export class PhoneInputFlagComponent implements ControlValueAccessor, OnChanges {
  private readonly host = inject(ElementRef<HTMLElement>);

  /** Alto de cada fila del dropdown (px), debe coincidir con la clase h-9 del botón. */
  readonly ITEM_SIZE = 36;
  /** Alto del viewport virtual: hasta 6 filas visibles, o menos si hay pocos resultados. */
  get viewportHeight(): number {
    return Math.min(this.filtered.length, 6) * this.ITEM_SIZE;
  }

  @Input() countries: PhoneCountryItem[] = [];
  @Input() placeholder = 'Número de teléfono';
  /** Código de país por defecto (ISO alpha-2) cuando no hay valor previo. */
  @Input() defaultCode = 'PE';

  countryCode = '';
  numberPart = '';
  open = false;
  query = '';
  disabled = false;

  private raw: string | null = null;
  private onChange: (v: string) => void = () => {};
  private onTouched: () => void = () => {};

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['countries'] && this.countries.length) {
      if (this.raw != null) {
        this.parse(this.raw);
      } else if (!this.countryCode) {
        this.countryCode = this.resolveDefault();
      }
    }
  }

  // ── ControlValueAccessor ────────────────────────────────────────────────
  writeValue(value: string | null): void {
    this.raw = value;
    if (this.countries.length) this.parse(value);
  }
  registerOnChange(fn: (v: string) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // ── State helpers ───────────────────────────────────────────────────────
  get selected(): PhoneCountryItem | null {
    return this.countries.find((c) => c.code === this.countryCode) ?? null;
  }

  get filtered(): PhoneCountryItem[] {
    const q = this.normalize(this.query.trim());
    if (!q) return this.countries;
    return this.countries.filter(
      (c) =>
        this.normalize(c.name).includes(q) ||
        c.code.toLowerCase().includes(q) ||
        c.dialCode.includes(q),
    );
  }

  toggle(): void {
    if (this.disabled) return;
    this.open = !this.open;
    if (this.open) this.query = '';
  }

  pick(c: PhoneCountryItem): void {
    this.countryCode = c.code;
    this.open = false;
    this.query = '';
    this.emit();
  }

  onNumberInput(value: string): void {
    this.numberPart = value;
    this.emit();
  }

  onBlur(): void {
    this.onTouched();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.open && !this.host.nativeElement.contains(event.target as Node)) {
      this.open = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.open = false;
  }

  // ── Internals ───────────────────────────────────────────────────────────
  private emit(): void {
    const num = this.numberPart.trim();
    if (!num) {
      this.onChange('');
      return;
    }
    const dial = this.selected?.dialCode ?? '';
    // Sin país seleccionado → se guarda el número tal cual (no se inventa código).
    this.onChange(dial ? `${dial} ${num}` : num);
  }

  /** Split a stored value like "+51 987654321" into country + number. */
  private parse(value: string | null): void {
    const s = (value ?? '').trim();
    if (!s) {
      this.numberPart = '';
      if (!this.countryCode) this.countryCode = this.resolveDefault();
      return;
    }
    if (s.startsWith('+')) {
      // Longest matching dial code wins (e.g. +591 before +59 / +5).
      const match = [...this.countries]
        .filter((c) => c.dialCode)
        .sort((a, b) => b.dialCode.length - a.dialCode.length)
        .find((c) => s.startsWith(c.dialCode));
      if (match) {
        this.countryCode = match.code;
        this.numberPart = s.slice(match.dialCode.length).trim();
        return;
      }
    }
    // Valor no vacío sin prefijo reconocible: NO se inventa país.
    // El prefijo queda sin seleccionar y el número se preserva tal cual;
    // al guardar se mantiene intacto salvo que el usuario elija un país.
    this.countryCode = '';
    this.numberPart = s;
  }

  private resolveDefault(): string {
    const byDefault = this.countries.find((c) => c.code === this.defaultCode);
    return byDefault?.code ?? this.countries[0]?.code ?? '';
  }

  private normalize(s: string): string {
    return s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  }
}
